/**
 * AdminStats Component
 * 
 * Displays key statistics cards for the admin dashboard
 * Shows metrics like total users, subscriptions, revenue, etc.
 * 
 * Features:
 * - Real-time data from backend API
 * - Loading states
 * - Error handling
 * - Responsive card layout
 */

import React from 'react';

const AdminStats = ({ stats, isLoading, error }) => {
  
  /**
   * Format currency values
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  /**
   * Format large numbers with commas
   */
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="admin-stats-grid">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="admin-stat-card loading">
            <div className="stat-skeleton"></div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="admin-error-box">
        <p>Error loading statistics: {error}</p>
      </div>
    );
  }

  // No data state
  if (!stats) {
    return null;
  }

  return (
    <div className="admin-stats-grid">
      {/* Total Users */}
      <div className="admin-stat-card">
        <div className="stat-icon">👥</div>
        <div className="stat-content">
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{formatNumber(stats.totalUsers || 0)}</div>
        </div>
      </div>

      {/* Active Subscriptions */}
      <div className="admin-stat-card">
        <div className="stat-icon">✓</div>
        <div className="stat-content">
          <div className="stat-label">Active Subscriptions</div>
          <div className="stat-value">{formatNumber(stats.activeSubscriptions || 0)}</div>
        </div>
      </div>

      {/* Total Revenue */}
      <div className="admin-stat-card highlight">
        <div className="stat-icon">💰</div>
        <div className="stat-content">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">{formatCurrency(stats.totalRevenue || 0)}</div>
        </div>
      </div>

      {/* Total Payments */}
      <div className="admin-stat-card">
        <div className="stat-icon">💳</div>
        <div className="stat-content">
          <div className="stat-label">Total Payments</div>
          <div className="stat-value">{formatNumber(stats.totalPayments || 0)}</div>
        </div>
      </div>

      {/* New Users This Month */}
      <div className="admin-stat-card success">
        <div className="stat-icon">📈</div>
        <div className="stat-content">
          <div className="stat-label">New Users This Month</div>
          <div className="stat-value">{formatNumber(stats.newUsersThisMonth || 0)}</div>
        </div>
      </div>

      {/* Failed Logins Today */}
      <div className="admin-stat-card warning">
        <div className="stat-icon">🔒</div>
        <div className="stat-content">
          <div className="stat-label">Failed Logins Today</div>
          <div className="stat-value">{formatNumber(stats.failedLoginsToday || 0)}</div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;

